import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, CloudSun, Globe, MapPin, Sparkles, TrendingUp } from "lucide-react";
import { Link } from "wouter";
import { getQualityScoreRange } from "@/lib/qualityScore";

const APP_VERSION = import.meta.env.VITE_APP_VERSION || "0.0.0";
const GIT_SHA = import.meta.env.VITE_GIT_SHA || "dev";

export default function Home() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: 'url(/hero-home.webp)' }}
        aria-label="Hero section"
        data-testid="hero-section"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/80" aria-hidden="true" />
        
        {/* Hero Content */}
        <div className="relative z-10 container text-center text-white px-4 py-20">
          <div className="max-w-4xl mx-auto space-y-8 animate-fade-in">
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight drop-shadow-lg">
              SunSquatcher
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light max-w-3xl mx-auto leading-relaxed drop-shadow-md">
              Predict the perfect sunrise and sunset moments with AI-powered precision
            </p>
            <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto drop-shadow-md">
              Harness advanced weather data and machine learning to discover spectacular golden hour conditions for breathtaking photography
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-6 sm:pt-8">
              <Link href="/forecast">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-xl shadow-2xl hover:shadow-primary/50 transition-all duration-300 hover:-translate-y-1"
                  aria-label="View sunrise and sunset forecast"
                >
                  <Sparkles className="mr-2 h-5 w-5" aria-hidden="true" />
                  View Forecast
                </Button>
              </Link>
              <Link href="/locations">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/80 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:-translate-y-1"
                  aria-label="View my saved locations"
                >
                  <MapPin className="mr-2 h-5 w-5" aria-hidden="true" />
                  My Locations
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Scroll Indicator */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full p-1">
              <div className="w-1.5 h-3 bg-white/70 rounded-full mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-background">
        <div className="container">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              Why SunSquatcher?
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              The most advanced sunrise and sunset quality prediction platform for photographers and nature enthusiasts
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {/* Feature 1 */}
            <Card className="card-elevated hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-2 border-none">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center shadow-lg" aria-hidden="true">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <CardTitle asChild>
                  <h3 className="text-2xl font-semibold">AI Predictions</h3>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-base leading-relaxed">
                  Advanced machine learning algorithms analyze atmospheric conditions to predict sunset and sunrise quality with exceptional accuracy
                </CardDescription>
              </CardContent>
            </Card>

            {/* Feature 2 */}
            <Card className="card-elevated hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-2 border-none">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-secondary to-primary rounded-2xl flex items-center justify-center shadow-lg" aria-hidden="true">
                  <CloudSun className="h-8 w-8 text-white" />
                </div>
                <CardTitle asChild>
                  <h3 className="text-2xl font-semibold">Weather Data</h3>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-base leading-relaxed">
                  Real-time weather data including cloud cover, humidity, visibility, and atmospheric pressure from trusted global sources
                </CardDescription>
              </CardContent>
            </Card>

            {/* Feature 3 */}
            <Card className="card-elevated hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-2 border-none">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-2xl flex items-center justify-center shadow-lg" aria-hidden="true">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <CardTitle asChild>
                  <h3 className="text-2xl font-semibold">Global Coverage</h3>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-base leading-relaxed">
                  Access predictions for any location worldwide, from iconic landmarks to hidden gems, all at your fingertips
                </CardDescription>
              </CardContent>
            </Card>

            {/* Feature 4 */}
            <Card className="card-elevated hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-2 border-none">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center shadow-lg" aria-hidden="true">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <CardTitle asChild>
                  <h3 className="text-2xl font-semibold">7-Day Forecasts</h3>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-base leading-relaxed">
                  Plan ahead with detailed 7-day forecasts showing quality scores, golden hour times, and optimal shooting conditions
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to discover perfect golden hour moments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12 max-w-5xl mx-auto">
            {/* Step 1 */}
            <div className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 gradient-golden rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-xl">
                1
              </div>
              <h3 className="text-2xl font-bold text-foreground">Search Location</h3>
              <p className="text-muted-foreground leading-relaxed">
                Enter any location worldwide or use your current position to get started
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 gradient-golden rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-xl">
                2
              </div>
              <h3 className="text-2xl font-bold text-foreground">View Predictions</h3>
              <p className="text-muted-foreground leading-relaxed">
                Get AI-powered quality scores and detailed weather data for the next 7 days
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 gradient-golden rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-xl">
                3
              </div>
              <h3 className="text-2xl font-bold text-foreground">Capture Beauty</h3>
              <p className="text-muted-foreground leading-relaxed">
                Plan your shoot with confidence and capture breathtaking sunrise and sunset moments
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Score Explanation */}
      <section className="py-16 sm:py-20 md:py-24 bg-background" data-testid="quality-section">
        <div className="container">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              Understanding Quality Scores
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Our AI analyzes atmospheric conditions to predict the visual quality of each sunrise and sunset
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
            {/* Vivid */}
            <Card className="overflow-hidden border-none shadow-xl" data-testid="quality-card-vivid">
              <div className="h-40 bg-cover bg-center" style={{ backgroundImage: 'url(/quality-vivid.webp)' }} role="img" aria-label="Example of vivid quality sunset with dramatic clouds and vibrant colors" />
              <CardHeader className="text-center bg-gradient-to-r from-amber-500 to-orange-600 text-white">
                <CardTitle asChild>
                  <h3 className="text-2xl font-bold">Vivid</h3>
                </CardTitle>
                <CardDescription className="text-white/90 font-semibold"><span className="sr-only">Quality score range: </span>{getQualityScoreRange("Vivid")}</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-center text-muted-foreground">
                  Spectacular conditions with dramatic clouds and vibrant colors. Perfect for photography.
                </p>
              </CardContent>
            </Card>

            {/* Great */}
            <Card className="overflow-hidden border-none shadow-xl" data-testid="quality-card-great">
              <div className="h-40 bg-cover bg-center" style={{ backgroundImage: 'url(/quality-great.webp)' }} role="img" aria-label="Example of great quality sunset with excellent conditions" />
              <CardHeader className="text-center bg-gradient-to-r from-orange-400 to-amber-500 text-white">
                <CardTitle asChild>
                  <h3 className="text-2xl font-bold">Great</h3>
                </CardTitle>
                <CardDescription className="text-white/90 font-semibold"><span className="sr-only">Quality score range: </span>{getQualityScoreRange("Great")}</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-center text-muted-foreground">
                  Excellent photography conditions with strong colors and good cloud coverage.
                </p>
              </CardContent>
            </Card>

            {/* Good */}
            <Card className="overflow-hidden border-none shadow-xl" data-testid="quality-card-good">
              <div className="h-40 bg-cover bg-center" style={{ backgroundImage: 'url(/quality-good.webp)' }} role="img" aria-label="Example of good quality sunset with pleasant conditions" />
              <CardHeader className="text-center bg-gradient-to-r from-yellow-400 to-orange-400 text-white">
                <CardTitle asChild>
                  <h3 className="text-2xl font-bold">Good</h3>
                </CardTitle>
                <CardDescription className="text-white/90 font-semibold"><span className="sr-only">Quality score range: </span>{getQualityScoreRange("Good")}</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-center text-muted-foreground">
                  Pleasant conditions with some color. Good opportunities for casual photography.
                </p>
              </CardContent>
            </Card>

            {/* Fair */}
            <Card className="overflow-hidden border-none shadow-xl" data-testid="quality-card-fair">
              <div className="h-40 bg-cover bg-center" style={{ backgroundImage: 'url(/quality-fair.webp)' }} role="img" aria-label="Example of fair quality sunset with moderate conditions" />
              <CardHeader className="text-center bg-gradient-to-r from-slate-400 to-slate-500 text-white">
                <CardTitle asChild>
                  <h3 className="text-2xl font-bold">Fair</h3>
                </CardTitle>
                <CardDescription className="text-white/90 font-semibold"><span className="sr-only">Quality score range: </span>{getQualityScoreRange("Fair")}</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-center text-muted-foreground">
                  Moderate quality with minimal color. Better than average but not exceptional.
                </p>
              </CardContent>
            </Card>

            {/* Poor */}
            <Card className="overflow-hidden border-none shadow-xl" data-testid="quality-card-poor">
              <div className="h-40 bg-cover bg-center" style={{ backgroundImage: 'url(/quality-poor.webp)' }} role="img" aria-label="Example of poor quality sunset with limited potential" />
              <CardHeader className="text-center bg-gradient-to-r from-slate-500 to-slate-600 text-white">
                <CardTitle asChild>
                  <h3 className="text-2xl font-bold">Poor</h3>
                </CardTitle>
                <CardDescription className="text-white/90 font-semibold"><span className="sr-only">Quality score range: </span>{getQualityScoreRange("Poor")}</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-center text-muted-foreground">
                  Limited potential due to overcast skies or unfavorable atmospheric conditions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-20 md:py-24 gradient-golden text-white">
        <div className="container text-center">
          <div className="max-w-3xl mx-auto space-y-6 sm:space-y-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">
              Ready to Capture the Perfect Moment?
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 leading-relaxed">
              Join photographers and nature enthusiasts worldwide who trust SunSquatcher for their golden hour planning
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-4">
              <Link href="/forecast">
                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-white/90 px-8 py-6 text-lg rounded-xl shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:-translate-y-1"
                  aria-label="Get started with SunSquatcher forecast"
                >
                  <Clock className="mr-2 h-5 w-5" aria-hidden="true" />
                  Get Started Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with deploy version marker */}
      <footer
        className="border-t border-border/60 bg-background py-6"
        data-testid="home-footer"
      >
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} SunSquatcher
          </p>
          <p
            className="text-xs font-mono text-muted-foreground/80"
            data-testid="app-version"
            title={`Package ${APP_VERSION}, commit ${GIT_SHA}`}
          >
            v{APP_VERSION}
            <span className="mx-1.5 text-muted-foreground/40" aria-hidden="true">
              ·
            </span>
            <span className="text-muted-foreground/70">{GIT_SHA}</span>
          </p>
        </div>
      </footer>
    </div>
  );
}
